// LoginPage.tsx
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import loginBG from "../../assets/loginBG.png";
import axiosInstance from "../../axiosInstance";
import { AppDispatch } from "../../redux/store";
import { useDispatch } from "react-redux";
import { GoogleLogin } from "@react-oauth/google";
import { fetchAndSaveSubscriptionsFromServer } from "../payment/SubscriptionInfo";
import { showToast } from "../../redux/features/toast/toast.slice";
import {
  clearAuthSession,
  getAccessToken,
  getRefreshToken,
  isTokenExpired,
  refreshAccessToken,
  setAccessToken,
  setRefreshToken,
} from "../../utils/auth";

function LoginPage() {
  const { state } = useLocation();
  const from = (state as any)?.from ?? "none";
  const message = (state as any)?.message ?? "none";
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  useEffect(() => {
    if (message !== "none") {
      dispatch(showToast({ message, type: "error" }));
    }
  }, [message, dispatch]);

  interface FormValues {
    email: string;
    password: string;
  }

  const [isVisible, setVisibility] = useState<boolean>(false);

  const [formValues, setFormValues] = useState<FormValues>({
    email: "",
    password: "",
  });

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // useEffect: Auto redirect if already logged in
  useEffect(() => {
    const checkAndRedirect = async () => {
      const accessToken = getAccessToken();
      const refreshToken = getRefreshToken();

      if (!accessToken || !refreshToken) {
        setLoading(false);
        return;
      } // Not logged in

      try {
        if (!isTokenExpired(accessToken)) {
          // Access token still valid
          navigate("/home");
          return;
        }

        // Otherwise try refreshing
        await refreshAccessToken();
        navigate("/home");
      } catch (err) {
        console.error("Token refresh failed:", err);
        clearAuthSession();
      } finally {
        setLoading(false);
      }
    };

    checkAndRedirect();
  }, [navigate]);

  const handleChangeVisibility = () => {
    setVisibility(!isVisible);
  };

  const handleTextFieldChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOnSubmit = async () => {
    try {
      const resp = await axiosInstance.post(
        `${process.env.REACT_APP_BASEURL}/users/auth/token/`,
        {
          email: formValues.email,
          password: formValues.password,
        },
      );

      // store tokens/user info (don't clear unrelated localStorage)
      setAccessToken(resp.data.access);
      setRefreshToken(resp.data.refresh);
      if (resp.data.user?.email)
        localStorage.setItem("email", resp.data.user.email);
      if (resp.data.user?.name)
        localStorage.setItem("name", resp.data.user.name);

      await fetchAndSaveSubscriptionsFromServer();

      navigate("/home");
    } catch (error: any) {
      clearAuthSession();
      console.error(error);
      const serverMsg =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        (typeof error?.response?.data === "string"
          ? error.response.data
          : null);
      if (error?.response?.status === 500) {
        setErrorMessage("Internal Server Error");
      } else {
        setErrorMessage(serverMsg || "Incorrect password or email");
      }
      setFormValues({ email: "", password: "" });
    }
  };

  const handleGoogleLogin = async (creds: any) => {
    try {
      const resp = await axiosInstance.post(
        `${process.env.REACT_APP_BASEURL}/users/auth/google/`,
        {
          id_token: creds.credential,
        },
      );

      // store tokens + user info
      setAccessToken(resp.data.access);
      setRefreshToken(resp.data.refresh);
      if (resp.data.user?.email)
        localStorage.setItem("email", resp.data.user.email);
      if (resp.data.user?.name)
        localStorage.setItem("name", resp.data.user.name);
      await fetchAndSaveSubscriptionsFromServer();
      navigate("/home");
    } catch (error: any) {
      clearAuthSession();
      console.error("Google login failed:", error);
      const serverMsg =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        (typeof error?.response?.data === "string"
          ? error.response.data
          : null);
      setErrorMessage(serverMsg || "Google login failed, please try again");
      setFormValues({ email: "", password: "" });
    }
  };

  const goToSignup = () => {
    navigate("/signup");
  };

  const goToForgotPass = () => {
    navigate("/forgotpass");
  };

  const isFormValid = !!formValues.email && !!formValues.password;
  if (loading) {
    return <>Loading...</>;
  }
  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          width: "100vw",
          background: `url(${loginBG}) , lightgray 50% / cover no-repeat`,
          backgroundSize: "cover",
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            padding: "40px 50px",
            flexDirection: "column",
            alignItems: "center",
            gap: "33px",
            borderRadius: "16px",
            background: "#FBE5C6",
            border: "2px solid #1a1a1a",
          }}
        >
          <Box sx={{ height: "91.204px", width: "105.313px" }}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="96"
              height="110"
              viewBox="0 0 96 110"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M48.0009 2L93.6018 28.3283V80.9839L48.0009 107.313L2.39819 80.9839V28.3283L48.0009 2Z"
                stroke="#FF9A56"
                strokeWidth="3.59941"
                strokeMiterlimit="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M21.5623 69.9565L65.9584 96.2337"
                stroke="#FF9A56"
                strokeWidth="3.78886"
                strokeMiterlimit="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M74.6055 69.9565L29.2772 96.2336"
                stroke="#FF9A56"
                strokeWidth="3.81813"
                strokeMiterlimit="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M47.9983 2V54.6563M47.9983 54.6563L2.94873 80.8096M47.9983 54.6563L93.6012 80.6264"
                stroke="#FF9A56"
                strokeWidth="3.59941"
                strokeMiterlimit="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M21.2759 18.6323L21.3536 50.033L21.3345 69.6664"
                stroke="#FF9A56"
                strokeWidth="3.78886"
                strokeMiterlimit="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M48.2749 3.3501L74.6075 69.9564"
                stroke="#FF9A56"
                strokeWidth="3.78886"
                strokeMiterlimit="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "5.5px",
            }}
          >
            <Typography
              sx={{
                display: "inline",
                color: "#282828",
                fontFeatureSettings: `'clig' off, 'liga' off`,
                fontFamily: "Poppins",
                fontSize: "26.4px",
                fontStyle: "normal",
                fontWeight: 500,
                lineHeight: "39.6px" /* 150% */,
              }}
            >
              Jump Right Back into
              <Typography
                component="span"
                sx={{
                  color: "#FF9A56",
                  fontFeatureSettings: `'clig' off, 'liga' off`,
                  fontFamily: "Poppins",
                  fontSize: "26.4px",
                  fontStyle: "normal",
                  fontWeight: 500,
                  lineHeight: "39.6px",
                }}
              >
                {" "}
                GPlan
              </Typography>
            </Typography>
            <Typography
              sx={{
                color: "#949CA9",
                /* SubText */
                fontFamily: "Poppins",
                fontSize: "16px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "24px" /* 150% */,
              }}
            >
              Login To Your Account
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "22px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                width: "506px",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "5.5px",
              }}
            >
              <Typography
                sx={{
                  alignSelf: "stretch",
                  color: "#282828",

                  /* SubText */
                  fontFamily: "Poppins",
                  fontSize: "16px",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "24px",
                }}
              >
                Email
              </Typography>
              <TextField
                fullWidth
                size="small"
                required
                InputLabelProps={{
                  shrink: false,
                  style: {
                    color: "#949CA9",
                    fontFamily: "Poppins",
                    fontSize: "14px",
                    fontStyle: "normal",
                    fontWeight: 400,
                    lineHeight: "24px",
                  },
                }}
                label={formValues.email === "" ? "Enter your email" : ""}
                name="email"
                value={formValues.email}
                onChange={handleTextFieldChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleOnSubmit();
                  }
                }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                width: "100%",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "5.5px",
              }}
            >
              <Typography
                sx={{
                  color: "#282828",

                  /* SubText */
                  fontFamily: "Poppins",
                  fontSize: "16px",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "24px",
                }}
              >
                Password
              </Typography>
              <TextField
                fullWidth
                required
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleChangeVisibility}
                        aria-label={
                          isVisible ? "Hide password" : "Show password"
                        }
                        edge="end"
                        size="small"
                      >
                        {isVisible ? (
                          <VisibilityOffOutlinedIcon />
                        ) : (
                          <RemoveRedEyeOutlinedIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{
                  shrink: false,
                  style: {
                    color: "#949CA9",
                    fontFamily: "Poppins",
                    fontSize: "14px",
                    fontStyle: "normal",
                    fontWeight: 400,
                    lineHeight: "24px",
                  },
                }}
                type={isVisible ? "text" : "password"}
                label={formValues.password === "" ? "Enter your password" : ""}
                name="password"
                value={formValues.password}
                onChange={handleTextFieldChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleOnSubmit();
                  }
                }}
              />
            </Box>
            {errorMessage && (
              <Typography
                sx={{
                  color: "red",
                  fontFamily: "Poppins",
                  fontSize: "14px",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "15px",
                  alignSelf: "stretch",
                }}
              >
                {errorMessage}
              </Typography>
            )}
            <Button
              fullWidth
              size="large"
              onClick={handleOnSubmit}
              variant="contained"
              disabled={!isFormValid}
              sx={{
                backgroundColor: isFormValid ? "#FF9A56" : "#CECECE",
                color: "#1a1a1a",
                border: "2px solid #1a1a1a",
                borderRadius: "12px",
                transition: "all 0.1s ease",
                "&:hover": {
                  backgroundColor: "#e8894d",
                },
              }}
            >
              <Typography
                sx={{
                  color: "#1a1a1a",
                  fontFamily: "Poppins",
                  fontSize: "18px",
                  fontStyle: "normal",
                  fontWeight: 700,
                  lineHeight: "26.4px",
                }}
              >
                Login
              </Typography>
            </Button>
          </Box>
          <Typography
            sx={{
              color: "#282828",
              fontFamily: "Poppins",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "24px",
            }}
          >
            {" "}
            New to GPlan?{" "}
            <Button onClick={goToSignup}>
              <Typography
                component="span"
                sx={{
                  color: "#FF9A56",

                  fontFamily: "Poppins",
                  fontSize: "16px",
                  fontStyle: "normal",
                  fontWeight: "400",
                  lineHeight: "24px",
                }}
              >
                Get Started
              </Typography>
            </Button>
            <Button onClick={goToForgotPass}>
              <Typography
                component="span"
                sx={{
                  color: "#FF9A56 ",

                  fontFamily: "Poppins",
                  fontSize: "16px",
                  fontStyle: "normal",
                  fontWeight: "400",
                  lineHeight: "24px",
                }}
              >
                Forgot Password
              </Typography>
            </Button>
          </Typography>
          <GoogleLogin
            onSuccess={(cred) => {
              handleGoogleLogin(cred);
            }}
            onError={() => {
              console.error("ERROR IN GOOGLE LOGIN");
            }}
            type="standard"
          />
        </Box>
      </Box>
    </>
  );
}

export default LoginPage;
